import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, PlusCircle, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Hub() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", image: "" });
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/hub/posts").then(({ data }) => setPosts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/hub/posts", form);
      setForm({ title: "", body: "", image: "" });
      setShowForm(false);
      toast.success("Posted to the Hub");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const like = async (id) => {
    setPosts((p) => p.map((x) => x.id === id ? { ...x, likes: x.likes + 1 } : x));
    try { await api.post(`/hub/posts/${id}/like`); } catch (e) { console.debug("like failed", e); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14" data-testid="hub-page">
      <div className="flex items-center gap-3 mb-2">
        <UsersThree weight="duotone" size={28} className="text-primary" />
        <p className="overline text-primary">Mr Hub</p>
      </div>
      <h1 className="text-4xl sm:text-5xl tracking-tighter font-light mb-2">Welcome back, {user?.name?.split(" ")[0]}.</h1>
      <p className="text-muted-foreground mb-10">The central member community. Share drops, wrap builds and fits with the crew.</p>

      {/* Dashboard cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        {[
          { label: "Community posts", value: posts.length },
          { label: "Your role", value: user?.role === "admin" ? "Admin" : "Member" },
          { label: "Total likes", value: posts.reduce((s, p) => s + (p.likes || 0), 0) },
          { label: "Status", value: "Active" },
        ].map((c) => (
          <div key={c.label} className="h-full p-5 rounded-xl bg-card border border-white/5" data-testid="hub-stat">
            <p className="text-2xl font-modern font-semibold text-primary">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-1 overline">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-modern font-medium">Community feed</h2>
        <button onClick={() => setShowForm((s) => !s)} className="inline-flex items-center gap-2 overline px-4 py-2 rounded-full border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="hub-new-post">
          <PlusCircle size={18} /> New post
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-8 p-6 rounded-xl bg-card border border-white/10 space-y-3" data-testid="hub-post-form">
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full bg-transparent border border-white/15 rounded-lg px-4 py-2.5 outline-none focus:border-primary transition-colors" data-testid="hub-post-title" />
          <textarea required rows={3} placeholder="What's happening?" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
            className="w-full bg-transparent border border-white/15 rounded-lg px-4 py-2.5 outline-none focus:border-primary transition-colors resize-none" data-testid="hub-post-body" />
          <input placeholder="Image URL (optional)" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
            className="w-full bg-transparent border border-white/15 rounded-lg px-4 py-2.5 outline-none focus:border-primary transition-colors" data-testid="hub-post-image" />
          <button disabled={loading} className="bg-primary text-primary-foreground px-6 py-2.5 rounded-full font-modern font-medium disabled:opacity-60" data-testid="hub-post-submit">
            {loading ? "Posting…" : "Post"}
          </button>
        </form>
      )}

      <div className="space-y-5" data-testid="hub-feed">
        {posts.map((p, i) => (
          <motion.article key={p.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: (i % 5) * 0.05 }}
            className="p-6 rounded-xl bg-card border border-white/5 hover:border-white/15 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-full bg-primary/20 grid place-items-center text-primary font-modern font-semibold">{(p.author || "?")[0]}</div>
              <div>
                <p className="font-modern font-medium text-sm">{p.author}</p>
                <p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            <h3 className="font-modern text-lg font-medium mb-1">{p.title}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed mb-3">{p.body}</p>
            {p.image && <div className="rounded-lg overflow-hidden mb-3 max-h-72"><img src={p.image} alt="" className="w-full object-cover" /></div>}
            <button onClick={() => like(p.id)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-accent transition-colors" data-testid={`hub-like-${p.id}`}>
              <Heart size={16} /> {p.likes}
            </button>
          </motion.article>
        ))}
      </div>
    </div>
  );
}
